import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  type MRT_TableInstance,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { dayRangeInstantsOf } from "_shared/_views/tables/table-date-range-filter";
import { applyUpdater } from "_shared/_views/tables/table-state-updater";
import { ToolbarButtonsContainer } from "_shared/_views/tables/toolbar-buttons";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ScrollableTabs,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import {
  useAcceptNetworkInvitationMutation,
  useCancelNetworkInvitationMutation,
  useGetUserNetworkInvitationsQuery,
  useRejectNetworkInvitationMutation,
} from "users/networking/connections/invitations/_api/network-invitation-api";
import NetworkInvitationQuery, {
  NetworkInvitationDirection,
  NetworkInvitationFilterParameters,
  NetworkInvitationOrderField,
  NetworkInvitationOrderParameters,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { DateCell } from "_shared/_views/tables/date-cells";
import { StatusChip, type StatusTone } from "_shared/_views/tables/status-chip";
import { formatDate } from "_shared/formatting/dates";

const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
// On a phone the chips get a line of their own under the tabs, as they do on Communications and
// Proposals. Sharing the row, they took the room the tab strip needed: the pair stacked in a 100px
// column beside it and left the four tabs 273px, so only "Pending" and part of its neighbours
// showed. `left` is not a value `align-items` takes either; it computed to `normal`.
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));
// The chips never shrink or wrap apart. On a phone they fill their line between them, as the
// Communications chips do, so the pair reads as one control rather than two stray pills.
const FilterButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    display: "grid",
    gridAutoColumns: "1fr",
    gridAutoFlow: "column",
    width: "100%",
  },
  "& > .MuiChip-root": {
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
      maxWidth: "none",
      width: "100%",
    },
  },
}));

const columnHelper = createMRTColumnHelper<NetworkInvitationInfo>();

/** The Name column's id: the server sorts it by the other person's last name, and filters by either name. */
export const NAME_COLUMN = "name";

/**
 * The Email column's id: the other person's address. The server neither sorts, filters nor searches by
 * it, and it folds under the name where the pane is short of room (#6722).
 */
export const EMAIL_COLUMN = "email";

/** The Sent column's id: the server sorts by when the invitation was sent, and filters by a range of days. */
export const SENT_COLUMN = "createdDate";

/** The Message column's id: the server sorts and filters it by the invitation's message. */
export const MESSAGE_COLUMN = "message";

/** The Status column's id. Every row on the tab is open, so the column neither sorts nor filters. */
export const STATUS_COLUMN = "status";

/** Newest first: the server's own default, so the header arrow says what order the rows are in. */
export const DEFAULT_SORTING: SortingState = [{ id: SENT_COLUMN, desc: true }];

/** What the table says when its column filters cannot be sent to the server. */
export const INVALID_FILTERS_MESSAGE = "The date range must end on or after the day it starts.";

/** The fields the sortable columns order by. A column missing here cannot be sorted. */
const ORDER_FIELDS: ReadonlyMap<string, NetworkInvitationOrderField> = new Map([
  [NAME_COLUMN, NetworkInvitationOrderField.Name],
  [SENT_COLUMN, NetworkInvitationOrderField.Sent],
  [MESSAGE_COLUMN, NetworkInvitationOrderField.Message],
]);

/**
 * The server's order, from the table's sort state (#5453). Each sortable column is mapped to a field
 * of the enum, so a column id is never sent as an order field: "createdDate" is not one. Always one
 * column and a direction, because the server orders every page.
 */
export function networkInvitationOrderFrom(sorting: SortingState): NetworkInvitationOrderParameters {
  const first = sorting.length > 0 ? sorting[0] : DEFAULT_SORTING[0];
  return new NetworkInvitationOrderParameters(
    ORDER_FIELDS.get(first.id) ?? NetworkInvitationOrderField.Sent,
    first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  );
}

/** The column filters the server takes. The sent range is undefined when it ends before it starts. */
export type NetworkInvitationColumnFilters = Pick<NetworkInvitationFilterParameters, "name" | "message" | "minSentDate" | "maxSentDate">;

/**
 * The server's column filters, from the table's, or undefined when they cannot be sent.
 *
 * The Sent filter is a range of days the reader picks where they are. It is sent as the first instant
 * of the first day and the last instant of the last, the server's `MinSentDate` and `MaxSentDate`.
 */
export function networkInvitationFiltersFrom(columnFilters: ColumnFiltersState): NetworkInvitationColumnFilters | undefined {
  const textOf = (id: string) => {
    const value = columnFilters.find((filter) => filter.id === id)?.value;
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
  };
  const sent = dayRangeInstantsOf(columnFilters.find((filter) => filter.id === SENT_COLUMN)?.value);
  if (!sent) return undefined;

  return {
    name: textOf(NAME_COLUMN),
    message: textOf(MESSAGE_COLUMN),
    minSentDate: sent.first ? new AHDate(sent.first.toISOString()) : undefined,
    maxSentDate: sent.last ? new AHDate(sent.last.toISOString()) : undefined,
  };
}

/**
 * The other person on the invitation: who it went to when the caller sent it, else who sent it.
 * Optional because MRT draws empty placeholder rows while a page loads.
 */
function counterpartyOf(invitation: NetworkInvitationInfo) {
  return invitation.isInviter ? invitation.invitee : invitation.inviter;
}

/**
 * Where an invitation opens: the other person's profile, on the list the reader is on. The row, the
 * name and the ⋯'s Open all go here.
 */
export function pendingInvitationDestination(invitation: NetworkInvitationInfo, context: string): string {
  return `/network/pending/view/${invitation.id}?context=${context}`;
}

/** The day the invitation was sent, as the Sent column shows it. */
function sentDateOf(invitation: NetworkInvitationInfo): AHDate | undefined {
  const created: unknown = invitation.created;
  return created instanceof AHDate ? created : undefined;
}

function sentDateText(invitation: NetworkInvitationInfo): string {
  return formatDate(sentDateOf(invitation)) ?? "";
}

/**
 * A network invitation's status, which the API sends in lower case ("pending"), as a status chip
 * (#6713). An open or pending invitation waits on the other person; an accepted one is settled.
 */
function NetworkInvitationStatusChip(props: Readonly<{ status: string | undefined }>) {
  const status = props.status ?? "";
  if (!status) return <NoValueDash />;
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const tone = ({ open: "info", pending: "info", accepted: "success" } as Record<string, StatusTone>)[status];
  return <StatusChip label={label} tone={tone ?? "neutral"} />;
}

export default function PendingTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;
  const context = searchParams.get("context") ?? "received";
  const sent = context === "sent";
  const received = context === "received";

  // Received and sent are separate lists on the server; a page index from one can run past the
  // end of the other. Reset during render, so the stale page is never requested.
  const [paginatedContextIsSent, setPaginatedContextIsSent] = React.useState(sent);
  if (paginatedContextIsSent !== sent) {
    setPaginatedContextIsSent(sent);
    if (pagination.pageIndex !== 0) setPagination({ ...pagination, pageIndex: 0 });
  }

  // #region Queries
  const [acceptInvitation] = useAcceptNetworkInvitationMutation();
  const [cancelInvitation] = useCancelNetworkInvitationMutation();
  const [rejectInvitation] = useRejectNetworkInvitationMutation();

  const columnFilterParameters = useMemo(() => networkInvitationFiltersFrom(columnFilters), [columnFilters]);

  const query = useMemo(() => {
    if (!columnFilterParameters) return undefined;

    const paginationParameters = PaginationParameters.create(pagination);
    // The server pages and counts only what the tab shows: open invitations in the chosen direction.
    // Filtering a page here instead would leave the footer counting rows nobody can see (#6283).
    const filterParameters = new NetworkInvitationFilterParameters({
      ...columnFilterParameters,
      direction: sent ? NetworkInvitationDirection.Sent : NetworkInvitationDirection.Received,
      status: "Open",
    });

    return new NetworkInvitationQuery({
      filterParameters,
      orderParameters: networkInvitationOrderFrom(sorting),
      paginationParameters,
      query: globalFilter,
    });
  }, [globalFilter, columnFilterParameters, pagination, sent, sorting]);

  const networkInvitationQuery = useGetUserNetworkInvitationsQuery(query ?? skipToken);
  // `currentData`, not `data`: while the other direction loads, `data` still holds the last answer,
  // which would show received invitations, with Accept and Reject, under the Sent chip.

  // The other person on each invitation, whose avatar and company the Name column draws (#6722). It
  // asked for the invitees whatever the direction, which on the received list is the reader themself.
  const invitationUserIds = useMemo(() => {
    const queryResult = networkInvitationQuery.currentData;
    if (!queryResult) return new Set<string>();
    return new Set(queryResult.data.map((invitation) => (invitation.isInviter ? invitation.invitee : invitation.inviter).id));
  }, [networkInvitationQuery.currentData]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    invitationUserIds?.size ? { userIds: [...invitationUserIds] } : skipToken,
  );

  const networkInvitations = useMemo(() => {
    const queryResult = networkInvitationQuery.currentData;

    if (!queryResult) {
      return PaginatedResponse.empty<NetworkInvitationInfo>();
    }

    return PaginatedResponse.from<NetworkInvitationInfo>(
      NetworkInvitationInfo.fromJSONArray(queryResult.data),
      queryResult.headers,
    );
  }, [networkInvitationQuery.currentData]);

  const handleViewProfile = useCallback(
    (individualId?: Guid, data?: NetworkInvitationInfo): void => {
      if (!individualId) return;

      let individualProfile: IndividualProfile | undefined;
      if (data instanceof UserInformation) {
        individualProfile = new IndividualProfile({
          userId: data.id,
          firstName: data.name.firstName ?? "",
          lastName: data.name.lastName ?? "",
          avatarId: data.avatarId,
          legalEntityId: data.legalEntityId,
          email: data.email,
        });
      } else if (data instanceof NetworkInvitationInfo) {
        const context = searchParams.get("context");
        const profileToShow = context === "sent" ? data.invitee : data.inviter;
        individualProfile = new IndividualProfile({
          userId: profileToShow.id,
          firstName: profileToShow.name.firstName ?? "",
          lastName: profileToShow.name.lastName ?? "",
          // TODO: The backend does not currently return an avatarId, it is always null
          avatarId: data.avatarId,
          email: profileToShow.email ?? undefined,
        });
      }

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
            profile={individualProfile}
          />
        ),
        onClose: () => {
          const context = searchParams.get("context") ?? "received";
          navigate(`/network/pending?context=${context}`);
          handledRef.current = null;
        }
      });
    },
    [openDialog, navigate, searchParams]
  );

  // Handle URL changes
  useEffect(() => {    
    let context = searchParams.get("context");
    if (context === null) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "received");
        return params;
      });
      return;
    }

    if (routeAction === "view" && routeId) {
      const key = `${routeAction}:${routeId}`;
      if (handledRef.current === key) return;

      const match = networkInvitations.data?.find(nc => nc.id?.value === routeId);
      if (!match?.inviter) return;

      handledRef.current = key;
      handleViewProfile(new Guid(match.inviter.id.value), match);
    }
  }, [handleViewProfile, networkInvitations.data, routeAction, routeId, searchParams, setSearchParams]);

  const renderPendingNameColumn = useCallback(
    (invitation: NetworkInvitationInfo, table: MRT_TableInstance<NetworkInvitationInfo>) => {
      const user = counterpartyOf(invitation);
      const individualJson = individualQuery.data?.find((ind) => ind.userId === user?.id.value);
      const individual = Individual.fromJSON(individualJson);
      // The company reads under the name, rather than in a column of its own (#6722).
      return (
        <NameCellContainer>
          <IndividualAvatar individual={individual ?? undefined} />
          <CondensedCell
            title={
              <RowLink to={pendingInvitationDestination(invitation, context)}>
                <SearchMatch text={user?.name.fullName} table={table} />
              </RowLink>
            }
            secondary={individual?.entityName}
          />
        </NameCellContainer>
      );
    },
    [context, individualQuery.data],
  );

  const pendingColumns = useMemo(
    () => [
      // Accessor columns, because TanStack never sorts or filters a display column (#5433). The
      // server sorts and filters; the accessors only let the table offer the controls.
      columnHelper.accessor((invitation) => counterpartyOf(invitation)?.name.fullName ?? "", {
        header: "Name",
        id: NAME_COLUMN,
        enableSorting: true,
        enableColumnFilter: true,
        meta: { kind: "name" },
        Cell: ({ row, table }) => renderPendingNameColumn(row.original, table),
      }),
      columnHelper.accessor((invitation) => counterpartyOf(invitation)?.email?.toString() ?? "", {
        header: "Email",
        id: EMAIL_COLUMN,
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        meta: {
          kind: "text",
          fold: { into: NAME_COLUMN, priority: 1, render: (invitation) => counterpartyOf(invitation)?.email?.toString() },
        },
        Cell: ({ row }) => counterpartyOf(row.original)?.email?.toString() || <NoValueDash />,
      }),
      columnHelper.accessor((invitation) => sentDateText(invitation), {
        header: "Sent",
        id: SENT_COLUMN,
        Cell: ({ row }) => <DateCell value={sentDateOf(row.original)} />,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "date-range",
        // The search matches the other person's name and the message, never a date or a status, so
        // neither column marks a match (#6590).
        enableGlobalFilter: false,
        meta: { kind: "date" },
      }),
      columnHelper.accessor((invitation) => invitation.message ?? "", {
        header: "Message",
        id: MESSAGE_COLUMN,
        enableSorting: true,
        enableColumnFilter: true,
        meta: { kind: "text" },
        // `||`, not `??`: an invitation sent without a message carries "" (`NetworkInvitationInfo`).
        // The rendered value is the message with the search term marked (#6590). A long one ends in an
        // ellipsis on one line, and its hover or keyboard focus shows the rest (#6722): the row opens the
        // person, not the message.
        Cell: ({ renderedCellValue, row }) =>
          row.original.message ? <TruncatedCell focusable>{renderedCellValue}</TruncatedCell> : <NoValueDash />,
      }),
      columnHelper.accessor((invitation) => invitation.status, {
        header: "Status",
        id: STATUS_COLUMN,
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        meta: { kind: "status" },
        Cell: ({ row }) => <NetworkInvitationStatusChip status={row.original.status} />,
      }),
    ],
    [renderPendingNameColumn],
  );

  async function handleAcceptInvitation(invitationId: Guid): Promise<void> {
    if (!invitationId) {
      await Promise.reject(new Error("No network invitation id provided"));
      return;
    }

    try {
      await acceptInvitation({ invitationId }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to accept invitation", { variant: "error" });
    }
  }

  async function handleRejectInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        throw new Error("No network invitation id provided");
      }

      await rejectInvitation({ invitationId }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject invitation", { variant: "error" });
    }
  }

  async function handleCancelInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        await Promise.reject(new Error("No network invitation id provided"));
        return;
      }

      const response = await confirm({
        title: "Cancel Network Connection Invitation?",
        message: "Are you sure you want to cancel this network connection invitation?",
        okButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (response === ConfirmResponse.Cancel) return;

      await cancelInvitation({ invitationId }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }

  function getPendingTabActions(row: Row<NetworkInvitationInfo>) {
    const invitation = row.original;
    // MRT draws placeholder rows with no invitation while the first page loads.
    if (!invitation?.id) return null;

    const counterparty = invitation.isInviter ? invitation.invitee : invitation.inviter;
    const counterpartyName = counterparty?.name.fullName ?? "this contact";
    const open = { to: pendingInvitationDestination(invitation, context) };
    // A settled invitation can still be opened, but no longer answered.
    if (invitation.status === "accepted" || invitation.status === "rejected") {
      return <RowActions ariaLabel={`Actions for ${counterpartyName}`} open={open} actions={[]} />;
    }

    const actions: RowAction[] = invitation.isInviter
      ? [
          {
            key: "cancel",
            label: "Cancel Invitation",
            ariaLabel: `Cancel invitation to ${counterpartyName}`,
            icon: <CancelOutlinedIcon />,
            destructive: true,
            onClick: () => {
              void handleCancelInvitation(invitation.id);
            },
          },
        ]
      : [
          {
            key: "accept",
            label: "Accept Invitation",
            ariaLabel: `Accept invitation from ${counterpartyName}`,
            icon: <CheckIcon />,
            onClick: () => {
              void handleAcceptInvitation(invitation.id);
            },
          },
          {
            key: "reject",
            label: "Reject Invitation",
            ariaLabel: `Reject invitation from ${counterpartyName}`,
            icon: <CloseIcon />,
            destructive: true,
            onClick: () => {
              void handleRejectInvitation(invitation.id);
            },
          },
        ];

    return <RowActions ariaLabel={`Actions for ${counterpartyName}`} open={open} actions={actions} />;
  }

  const tableOptions: MRT_TableOptions<NetworkInvitationInfo> = {
    columns: pendingColumns,
    data: networkInvitations.data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => getPendingTabActions(row),
    renderToolbarInternalActions: ({ table }) =>
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <TableToolsMenu table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={PageTab.Pending}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </ScrollableTabs>
            <FilterButtons>
              <Chip
                icon={<FilterListIcon />}
                label="Received"
                color="primary"
                variant={received ? "filled" : "outlined"}
                onClick={() => {
                  if (received) return;
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    params.set("context", "received");
                    return params;
                  });
                }}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Sent"
                color="primary"
                variant={sent ? "filled" : "outlined"}
                onClick={() => {
                  if (sent) return;
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    params.set("context", "sent");
                    return params;
                  });
                }}
              />
            </FilterButtons>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "pending requests", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: networkInvitationQuery.isError,
      isLoading: networkInvitationQuery.isFetching,
      onRetry: () => void networkInvitationQuery.refetch(),
      subject: "pending requests",
    }),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    // The server orders by one column, and always by one, so there is neither a second sort nor none.
    enableMultiSort: false,
    enableSortingRemoval: false,
    // A range that ends before it starts is not sent, so the table says why nothing changed.
    renderBottomToolbarCustomActions: () => columnFilterParameters
      ? null
      : <Typography color="error" variant="body2" role="alert">{INVALID_FILTERS_MESSAGE}</Typography>,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(pendingInvitationDestination(row.original, context));
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: () => ({
      sx: {
        flex: 1
      },
    }),
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: networkInvitationQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: networkInvitations.totalElements,
    // A new search, filter or order is a new list, and a new list starts on its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    state: {
      globalFilter,
      columnFilters,
      isLoading: networkInvitationQuery.isLoading,
      pagination,
      showAlertBanner: networkInvitationQuery.isError,
      showProgressBars: networkInvitationQuery.isFetching,
      showSkeletons: networkInvitationQuery.isLoading,
      sorting,
    },
  }

  return <MRTNoMemo<NetworkInvitationInfo> options={tableOptions} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
